import type { SignedDocumentSummary, WorkOrderForm344 } from '@railroute/contracts/core';

// Ячейки инструктажа в листе наряда (пункт 11 приложения 1 Правил № 344),
// issue #533 по решению Штаба 29.09.2026 (#531).
//
// По каждому работнику — отдельные строки:
//   - кто провёл инструктаж, результат проверки знаний и подпись проводившего;
//     повторный инструктаж после «не прошёл» — СВОЕЙ строкой, первая остаётся;
//   - подпись работника или «подписано на бумаге» с фото журнала;
//   - прежние отметки «за работника» — как есть, с пометкой, что способ отменён.
// Каждая подпись ключом устройства названа «без ЭЦП», пока сервер не скажет,
// что это ЭЦП: выдать её за юридически значимую лист не вправе.

type CrewMember = WorkOrderForm344['crew'][number];

export type BriefingLine =
  | { kind: 'conduct'; at: string | null; byName: string; passed: boolean | null; qualified: boolean }
  | { kind: 'unsigned'; at: string | null; byName: string | null }
  | { kind: 'self'; at: string | null; qualified: boolean }
  | { kind: 'paper'; at: string | null; byName: string | null }
  | { kind: 'proxy'; at: string | null; byName: string | null };

const ACCEPTED = new Set<SignedDocumentSummary['status']>(['SIGNED', 'PENDING']);

export function briefingLines(member: CrewMember, documents: readonly SignedDocumentSummary[]): BriefingLine[] {
  const lines: BriefingLine[] = [];
  const conducts = documents
    .filter(
      (d) =>
        d.documentKind === 'TARGET_BRIEFING_CONDUCT' &&
        ACCEPTED.has(d.status) &&
        d.onBehalfOfUserId === member.workerId,
    )
    .sort((a, b) => (a.signedAt ?? a.requestedAt).localeCompare(b.signedAt ?? b.requestedAt));
  for (const d of conducts) {
    lines.push({
      kind: 'conduct',
      at: d.signedAt ?? null,
      byName: d.signedByNameSnapshot,
      passed: d.briefingKnowledgePassed ?? null,
      qualified: d.signatureLevel === 'QUALIFIED_ECP',
    });
  }
  // Отметка без подписи (до #531 и в городском контуре): строка бригады
  // помнит последнюю. Если по ней есть подписанное проведение — это оно.
  if (conducts.length === 0 && member.targetBriefingDoneAt) {
    lines.push({ kind: 'unsigned', at: member.targetBriefingDoneAt, byName: member.targetBriefingByName ?? null });
  }
  if (member.targetBriefingAckAt) {
    if (member.targetBriefingAckMethod === 'WORKER_DEVICE_KEY') {
      const ack = documents.find(
        (d) => d.documentKind === 'TARGET_BRIEFING_ACK' && ACCEPTED.has(d.status) && d.signedByUserId === member.workerId,
      );
      lines.push({ kind: 'self', at: member.targetBriefingAckAt, qualified: ack?.signatureLevel === 'QUALIFIED_ECP' });
    } else if (member.targetBriefingAckMethod === 'PAPER_JOURNAL') {
      lines.push({
        kind: 'paper',
        at: member.targetBriefingPaperMark?.uploadedAt ?? member.targetBriefingAckAt,
        byName: member.targetBriefingPaperMark?.uploadedByName ?? member.targetBriefingAckProxyName,
      });
    } else if (member.targetBriefingAckMethod === 'PROXY_VERBAL') {
      lines.push({ kind: 'proxy', at: member.targetBriefingAckAt, byName: member.targetBriefingAckProxyName });
    }
  }
  return lines;
}

/** Строка ячейки словами; `moment` — формат времени листа. */
export function briefingLineText(line: BriefingLine, moment: (iso: string | null) => string): string {
  const ecp = (qualified: boolean) => (qualified ? 'ЭЦП' : 'без ЭЦП');
  switch (line.kind) {
    case 'conduct':
      return [
        `Инструктаж провёл: ${line.byName}, ${moment(line.at)}`,
        `проверка знаний: ${line.passed === true ? 'прошёл' : line.passed === false ? 'не прошёл' : '—'}`,
        `подпись проводившего ${ecp(line.qualified)}`,
      ].join(' · ');
    case 'unsigned':
      return `Инструктаж отмечен без подписи${line.byName ? `: ${line.byName}` : ''}, ${moment(line.at)}`;
    case 'self':
      return `Работник подписал сам: ${moment(line.at)} · ${ecp(line.qualified)}`;
    case 'paper':
      return `Подписано на бумаге (фото журнала)${line.byName ? `, загрузил ${line.byName}` : ''}: ${moment(line.at)}`;
    case 'proxy':
      return `Отметка за работника — способ отменён${line.byName ? ` (${line.byName})` : ''}: ${moment(line.at)}`;
  }
}
